import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getConsent, saveConsent as saveConsentService } from '../services/CookieConsentService';
import { useAuth } from '../components/AuthProvider';

// Create context
export const CookieConsentContext = createContext();

// Initial state
const initialState = {
  necessary: true,
  analytics: false,
  marketing: false,
  isLoaded: false,
  hasConsent: false // Whether user has already expressed consent
};

// Helper function to get or create session ID
const getSessionId = () => {
  let sessionId = localStorage.getItem('cookieSessionId');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('cookieSessionId', sessionId);
  }
  return sessionId;
};

// Helper function to save to localStorage
const saveToLocalStorage = (consent) => {
  const data = {
    necessary: consent.necessary,
    analytics: consent.analytics,
    marketing: consent.marketing,
    timestamp: new Date().toISOString()
  };
  localStorage.setItem('cookieConsent', JSON.stringify(data));
};

// Helper function to load from localStorage
const loadFromLocalStorage = () => {
  try {
    const stored = localStorage.getItem('cookieConsent');
    if (stored) {
      const data = JSON.parse(stored);
      return {
        necessary: data.necessary !== undefined ? data.necessary : true,
        analytics: data.analytics !== undefined ? data.analytics : false,
        marketing: data.marketing !== undefined ? data.marketing : false,
        hasConsent: true
      };
    }
  } catch (error) {
    console.error('Error loading cookie consent from localStorage:', error);
  }
  return { ...initialState, hasConsent: false };
};

// Create provider component
export const CookieConsentProvider = ({ children }) => {
  const { isLoggedIn, recId } = useAuth();
  const [consent, setConsent] = useState(initialState);
  const [sessionId] = useState(() => getSessionId());

  // Load consent on mount and when auth state changes
  const loadConsent = useCallback(async () => {
    try {
      // First check localStorage
      const localConsent = loadFromLocalStorage();
      
      if (isLoggedIn && recId) {
        // For logged-in users, try to load from API
        try {
          const { data: apiConsent, meta } = await getConsent();
          if (meta) {
            console.warn('Cookie consent warning:', meta);
          }
          if (apiConsent && (apiConsent.necessary !== undefined || apiConsent.analytics !== undefined || apiConsent.marketing !== undefined)) {
            // API has preferences, use them
            setConsent({
              necessary: apiConsent.necessary !== undefined ? apiConsent.necessary : true,
              analytics: apiConsent.analytics !== undefined ? apiConsent.analytics : false,
              marketing: apiConsent.marketing !== undefined ? apiConsent.marketing : false,
              isLoaded: true,
              hasConsent: true
            });
            // Sync to localStorage
            saveToLocalStorage({
              necessary: apiConsent.necessary !== undefined ? apiConsent.necessary : true,
              analytics: apiConsent.analytics !== undefined ? apiConsent.analytics : false,
              marketing: apiConsent.marketing !== undefined ? apiConsent.marketing : false
            });
            return;
          } else if (localConsent.hasConsent) {
            // No API preferences but localStorage has them, sync to API
            const { meta: saveMeta } = await saveConsentService({
              necessary: localConsent.necessary,
              analytics: localConsent.analytics,
              marketing: localConsent.marketing
            });
            if (saveMeta) {
              console.warn('Cookie consent save warning:', saveMeta);
            }
            setConsent({
              ...localConsent,
              isLoaded: true
            });
            return;
          }
        } catch (error) {
          console.error('Error loading consent from API:', error);
          // Fall back to localStorage
        }
      } else {
        // For anonymous users, use localStorage only
        setConsent({
          ...localConsent,
          isLoaded: true
        });
        return;
      }
      
      // Default: no consent yet
      setConsent({
        ...initialState,
        isLoaded: true
      });
    } catch (error) {
      console.error('Error loading cookie consent:', error);
      setConsent({
        ...initialState,
        isLoaded: true
      });
    }
  }, [isLoggedIn, recId]);

  // Load consent on mount
  useEffect(() => {
    loadConsent();
  }, [loadConsent]);

  // Save consent
  const saveConsent = useCallback(async (newConsent) => {
    try {
      // Ensure necessary is always true
      const consentToSave = {
        necessary: true,
        analytics: newConsent.analytics !== undefined ? newConsent.analytics : false,
        marketing: newConsent.marketing !== undefined ? newConsent.marketing : false
      };

      // Save to localStorage
      saveToLocalStorage(consentToSave);

      // Save to API if logged in
      if (isLoggedIn && recId) {
        try {
          const { meta } = await saveConsentService(consentToSave);
          if (meta) {
            console.warn('Cookie consent save warning:', meta);
          }
        } catch (error) {
          console.error('Error saving consent to API:', error);
        }
      } else {
        // For anonymous users, save with sessionId
        try {
          const { meta } = await saveConsentService(consentToSave, sessionId);
          if (meta) {
            console.warn('Cookie consent save warning:', meta);
          }
        } catch (error) {
          console.error('Error saving consent to API with sessionId:', error);
        }
      }

      setConsent({
        ...consentToSave,
        isLoaded: true,
        hasConsent: true
      });
    } catch (error) {
      console.error('Error saving cookie consent:', error);
    }
  }, [isLoggedIn, recId, sessionId]);

  // Accept all cookies
  const acceptAll = useCallback(() => {
    saveConsent({
      necessary: true,
      analytics: true,
      marketing: true
    });
  }, [saveConsent]);

  // Reject optional cookies (only necessary)
  const rejectOptional = useCallback(() => {
    saveConsent({
      necessary: true,
      analytics: false,
      marketing: false
    });
  }, [saveConsent]);

  // Value to be provided
  const value = {
    consent: {
      necessary: consent.necessary,
      analytics: consent.analytics,
      marketing: consent.marketing
    },
    isLoaded: consent.isLoaded,
    hasConsent: consent.hasConsent,
    loadConsent,
    saveConsent,
    acceptAll,
    rejectOptional
  };

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
};

// Custom hook for using cookie consent
export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext);

  if (!context) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }

  return context;
};

